import {
    CurrencyCode,
    InvoiceStatus,
    SubscriptionPlanKey,
    SubscriptionPlanHelpers,
    UserAccountType,
    PaymentSourceBrand,
    PaymentType
} from "@authory-packages/core";
import { AuthenticatedUser } from "../../../types/user";
import { AUTHORY_2023_PLAN_TIERS } from "../../../utils/pricingPerks";
import { formatUSD } from "../../../utils/getPlanPrice";

const getSourceBrand = (source: PaymentSourceBrand) => {
    switch (source) {
        case PaymentSourceBrand.Visa:
            return "Visa";
        case PaymentSourceBrand.AmericanExpress:
            return "American Express";
        case PaymentSourceBrand.Bancontact:
            return "BanContact";
        case PaymentSourceBrand.DinersClub:
            return "DinersClub";
        case PaymentSourceBrand.Discover:
            return "Discover";
        case PaymentSourceBrand.Mastercard:
            return "Mastercard";
        case PaymentSourceBrand.Other:
        default:
            return "Card";
    }
};

export const getPaymentMethod = (
    method: PaymentType | null | undefined,
    paymentSourceLastFourDigits: string | null | undefined,
    paymentSourceBrand: PaymentSourceBrand | null | undefined
) => {
    if (method === PaymentType.ChargebeePaypal) return "PayPal";

    if (method === PaymentType.ChargebeeStripe) {
        if (paymentSourceBrand && paymentSourceLastFourDigits)
            return `${getSourceBrand(paymentSourceBrand)} ending on ${paymentSourceLastFourDigits}`;
        return "Card";
    }

    if (method === PaymentType.ChargebeeApplePay) return "Apple Pay";
    if (method === PaymentType.ChargebeeGooglePay) return "Google Pay";

    return "-";
};

export const getPlanDuration = (planKey: SubscriptionPlanKey) => {
    switch (planKey) {
        case SubscriptionPlanKey.DiscountedUSDMonthly:
        case SubscriptionPlanKey.USDMonthly:
        case SubscriptionPlanKey.USDAuthorySub2023Monthly:
        case SubscriptionPlanKey.USDAuthoryLiteMonthly:
        case SubscriptionPlanKey.USDAuthoryProSub2023Monthly:
            return "Monthly";
        case SubscriptionPlanKey.DiscountedUSDYearly:
        case SubscriptionPlanKey.USDYearly:
        case SubscriptionPlanKey.USDAuthorySub2023Yearly:
        case SubscriptionPlanKey.USDAuthoryProSub2023Yearly:
        case SubscriptionPlanKey.USDAuthoryLiteYearly:
            return "Yearly";
        case SubscriptionPlanKey.USDAuthoryFreeSub2023Yearly:
        case SubscriptionPlanKey.USDAuthoryFreeProSub2023Yearly:
        case SubscriptionPlanKey.USDAuthoryFreeProSub2023Monthly:
        case SubscriptionPlanKey.USDAuthoryFreeSub2023Monthly:
            return "";
        default:
            return "-";
    }
};

export const getPlanCostMultiplier = (planKey: SubscriptionPlanKey) => {
    switch (planKey) {
        case SubscriptionPlanKey.USDAuthoryFreeProSub2023Monthly:
        case SubscriptionPlanKey.USDAuthoryFreeSub2023Monthly:
        case SubscriptionPlanKey.DiscountedUSDMonthly:
        case SubscriptionPlanKey.USDMonthly:
        case SubscriptionPlanKey.USDAuthorySub2023Monthly:
        case SubscriptionPlanKey.USDAuthoryLiteMonthly:
        case SubscriptionPlanKey.USDAuthoryProSub2023Monthly:
            return 1;
        case SubscriptionPlanKey.USDAuthoryFreeProSub2023Yearly:
        case SubscriptionPlanKey.USDAuthoryLiteYearly:
        case SubscriptionPlanKey.USDAuthoryFreeSub2023Yearly:
        case SubscriptionPlanKey.DiscountedUSDYearly:
        case SubscriptionPlanKey.USDYearly:
        case SubscriptionPlanKey.USDAuthorySub2023Yearly:
        case SubscriptionPlanKey.USDAuthoryProSub2023Yearly:
            return 12;
        default:
            return 1;
    }
};

export const getPlanDurationPeriod = (planKey: SubscriptionPlanKey) => {
    switch (planKey) {
        case SubscriptionPlanKey.DiscountedUSDMonthly:
        case SubscriptionPlanKey.USDMonthly:
        case SubscriptionPlanKey.USDAuthorySub2023Monthly:
        case SubscriptionPlanKey.USDAuthoryProSub2023Monthly:
        case SubscriptionPlanKey.USDAuthoryLiteMonthly:
        case SubscriptionPlanKey.USDAuthoryFreeProSub2023Monthly:
        case SubscriptionPlanKey.USDAuthoryFreeSub2023Monthly:
            return "per month";
        case SubscriptionPlanKey.DiscountedUSDYearly:
        case SubscriptionPlanKey.USDYearly:
        case SubscriptionPlanKey.USDAuthoryLiteYearly:
        case SubscriptionPlanKey.USDAuthorySub2023Yearly:
        case SubscriptionPlanKey.USDAuthoryProSub2023Yearly:
        case SubscriptionPlanKey.USDAuthoryFreeProSub2023Yearly:
        case SubscriptionPlanKey.USDAuthoryFreeSub2023Yearly:
            return "per year";
        default:
            return "-";
    }
};

export const getV3PlanAmount = (subscriptionAmountCents: number | null | undefined, subscriptionCurrency: CurrencyCode | null | undefined) => {
    if (subscriptionAmountCents && subscriptionCurrency) return `${subscriptionCurrency} ${subscriptionAmountCents / 100}`;
    return "";
};

export const getInvoiceStatus = (status: InvoiceStatus) => {
    switch (status) {
        case InvoiceStatus.Not_paid:
            return "Unpaid";
        case InvoiceStatus.Paid:
            return "Paid";
        case InvoiceStatus.Payment_due:
            return "Payment Due";
        case InvoiceStatus.Posted:
            return "Posted";
        case InvoiceStatus.Voided:
            return "Voided";
        case InvoiceStatus.pending:
            return "Pending";
        default:
            return "-";
    }
};

export const getPaymentAddressLabel = (
    billingAddressLineOne: string | null | undefined,
    billingAddressLineTwo: string | null | undefined,
    billingAddressState: string | null | undefined,
    billingAddressCountry: string | null | undefined,
    billingAddressZip: string | null | undefined
) => {
    const address = `${billingAddressLineOne || ""} ${billingAddressLineTwo || ""} ${billingAddressState || ""} ${billingAddressCountry || ""} ${
        billingAddressZip || ""
    }`;
    const trimmedAddress = address.substring(0, 40);
    return trimmedAddress.trim().length ? `${trimmedAddress.trim()}...` : "No billing address set";
};

export const V3HasAccessToBillingPage = (user: AuthenticatedUser) => {
    const isBusinessParent = user?.type === UserAccountType.BusinessParent;
    const isBusinessSeat = user?.type === UserAccountType.BusinessSeat;
    const isBusiness = isBusinessParent || isBusinessSeat;

    return !isBusiness;
};

export const getPlanMetrics = (planSub?: SubscriptionPlanKey | null) => {
    const isProMonthly = planSub === SubscriptionPlanKey.USDAuthoryProSub2023Monthly;
    const isProYearly = planSub === SubscriptionPlanKey.USDAuthoryProSub2023Yearly;
    const isRegularMonthly = planSub === SubscriptionPlanKey.USDAuthorySub2023Monthly;
    const isRegularYearly = planSub === SubscriptionPlanKey.USDAuthorySub2023Yearly;
    const isLiteYearly = planSub === SubscriptionPlanKey.USDAuthoryLiteYearly;
    const isLiteMonhtly = planSub === SubscriptionPlanKey.USDAuthoryLiteMonthly;
    const isLite = isLiteYearly || isLiteMonhtly;
    const isPro = isProMonthly || isProYearly;
    const isRegular = isRegularMonthly || isRegularYearly || planSub === null;
    const isYearly = !isRegularMonthly && !isProMonthly && !isLiteMonhtly;
    const tier = isPro
        ? AUTHORY_2023_PLAN_TIERS.PRO
        : isRegular
          ? AUTHORY_2023_PLAN_TIERS.STANDARD
          : isLite
            ? AUTHORY_2023_PLAN_TIERS.LITE
            : AUTHORY_2023_PLAN_TIERS.FREE;

    return {
        tier,
        isYearly,
        isPro
    };
};

export const getChargeCopy = (perMonth: number, perYear: number, isYearly: boolean, useComma = false) =>
    useComma
        ? `${formatUSD(perMonth)} per month${isYearly ? `, billed at ${formatUSD(perYear)} yearly` : ""}`
        : `${formatUSD(perMonth)} per month${isYearly ? ` (billed at ${formatUSD(perYear)} yearly)` : ""}`;

/**
 * Whether switching from `currentPlan` to `targetPlan` is purely a change of billing term
 * (monthly <-> yearly) within the same plan tier, rather than a move to a different tier.
 */
export const isSameTierBillingTermChange = (currentPlan: SubscriptionPlanKey | null | undefined, targetPlan: SubscriptionPlanKey): boolean => {
    if (!currentPlan) return false;

    const legacyPlans = [
        SubscriptionPlanKey.DiscountedUSDMonthly,
        SubscriptionPlanKey.DiscountedUSDYearly,
        SubscriptionPlanKey.USDMonthlyLegacy7,
        SubscriptionPlanKey.USDYearlyLegacy7
    ];

    if (legacyPlans.includes(currentPlan)) return false;

    const sameTier = SubscriptionPlanHelpers.getPlanAccessLevel(currentPlan) === SubscriptionPlanHelpers.getPlanAccessLevel(targetPlan);
    if (!sameTier) return false;

    return getPlanDuration(currentPlan) !== getPlanDuration(targetPlan);
};
